import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { BlogList } from "@/components/site/BlogViews";
import { CtaBand, PageHero } from "@/components/site/Page";
import { InstagramFollowCta } from "@/components/site/Instagram";
import { listPublishedPosts } from "@/lib/blog.functions";
import { getInstagramFeed } from "@/lib/instagram.functions";
import { safeLoader } from "@/lib/safe-loader";

const TITLE = "Bloggen – Swedish International School i Spanien";
const DESCRIPTION =
  "Texter från vardagen på svenska skolan i Estepona och Marbella: skolliv, undervisning, idrott och råd till familjer som flyttar till Costa del Sol.";

export const Route = createFileRoute("/blogg/")({
  loader: async ({ context }) => {
    const [posts, instagram] = await Promise.all([
      safeLoader("sv.blog.posts", () => context.queryClient.ensureQueryData({
        queryKey: ["blog-list", "sv"],
        queryFn: () => listPublishedPosts({ data: { locale: "sv", limit: 24 } }),
      }), []),
      safeLoader("sv.blog.instagram", () => context.queryClient.ensureQueryData({
        queryKey: ["instagram-feed"],
        queryFn: () => getInstagramFeed(),
      }), { posts: [], highlights: [] }),
    ]);
    return { posts, instagramPosts: instagram.posts };
  },
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/blogg") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/blogg"),
  }),
  errorComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">
      Bloggen kunde inte laddas just nu. Försök igen om en stund.
    </div>
  ),
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-20 text-sm text-muted-foreground">Sidan finns inte.</div>
  ),
  component: BlogIndex,
});

function BlogIndex() {
  const { posts, instagramPosts } = Route.useLoaderData();
  return (
    <>
      <PageHero
        eyebrow="Blogg"
        title="Från skolans vardag"
        intro="Här skriver vi om undervisning, skolliv och livet på Costa del Sol – texter som vi själva hade velat läsa innan flytten."
      />

      <BlogList posts={posts} locale="sv" />

      <div className="py-10 sm:py-14">
        <InstagramFollowCta posts={instagramPosts} locale="sv" />
      </div>

      <CtaBand locale="sv" />
    </>
  );
}
